import React, { useState } from "react";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Link,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import {
  Datagrid,
  DateField,
  List,
  Resource,
  Show,
  SimpleShowLayout,
  TextField,
  useNotify,
  useRecordContext,
  useRefresh,
} from "react-admin";
import { useTokenOps } from "../components/TokenContext";
import { carbonRequest } from "./corporateCarbon/api";
import {
  CarbonAdminPage,
  CarbonDataTable,
  CarbonEditSurface,
  CarbonFileInput,
  CarbonForm,
  CarbonFormToolbar,
  CarbonSection,
  CarbonTextInput,
} from "./corporateCarbon/CarbonAdminPage";

export const allowedFactorActions = (status) => ({
  validate: status === "draft",
  submitReview: status === "draft",
  publish: status === "in_review",
  retire: status === "published",
});

export const FactorReleaseStatusActions = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const tokenOps = useTokenOps();
  const [pendingAction, setPendingAction] = useState(null);
  const [notes, setNotes] = useState("");
  if (!record) return null;
  const actions = allowedFactorActions(record.status);
  const run = async (action, label, actionNotes = "") => {
    try {
      let body;
      if (["submit-review", "publish", "retire"].includes(action)) {
        if (!actionNotes.trim()) return notify("Notes are required", "warning");
        body = JSON.stringify({
          notes: actionNotes.trim(),
          ...(action === "retire"
            ? {}
            : {
                reviewChecksum: record.draftChecksum || record.reviewChecksum,
              }),
        });
      }
      await carbonRequest(
        tokenOps.account.accessToken,
        `corporate-carbon/factor-releases/${record.id}/${action}`,
        { method: "POST", body },
      );
      notify(`${label} completed`, "info");
      setPendingAction(null);
      setNotes("");
      refresh();
    } catch (error) {
      notify(error.message, "warning");
    }
  };
  const requestAction = (action, label) => {
    setNotes("");
    setPendingAction({ action, label });
  };
  return (
    <CardActions>
      {actions.validate && (
        <Button onClick={() => run("validate", "Validation")}>Validate</Button>
      )}
      {actions.submitReview && (
        <Button
          color="primary"
          onClick={() => requestAction("submit-review", "Review submission")}
        >
          Submit for review
        </Button>
      )}
      {actions.publish && (
        <Button
          color="primary"
          variant="contained"
          onClick={() => requestAction("publish", "Publication")}
        >
          Publish
        </Button>
      )}
      {actions.retire && (
        <Button color="secondary" onClick={() => requestAction("retire", "Retirement")}>
          Retire
        </Button>
      )}
      <CarbonEditSurface
        description={pendingAction?.action === "publish" ? "Publishing makes this factor release immutable." : "Record why this lifecycle change is being made."}
        onClose={() => setPendingAction(null)}
        open={Boolean(pendingAction)}
        title={pendingAction?.action === "publish" ? "Publish factor release?" : `${pendingAction?.label || "Update"} factor release`}
      >
        <CarbonForm
          aria-label="Factor release lifecycle form"
          onSubmit={(event) => {
            event.preventDefault();
            if (pendingAction) run(pendingAction.action, pendingAction.label, notes);
          }}
        >
          <CarbonTextInput
            label={`${pendingAction?.label || "Action"} notes`}
            multiline
            name="lifecycleNotes"
            onChange={(event) => setNotes(event.target.value)}
            required
            value={notes}
          />
          <CarbonFormToolbar>
            <Button color="primary" disabled={!notes.trim()} type="submit" variant="contained">
              {pendingAction?.action === "publish" ? "Publish release" : pendingAction?.label}
            </Button>
            <Button onClick={() => setPendingAction(null)}>Cancel</Button>
          </CarbonFormToolbar>
        </CarbonForm>
      </CarbonEditSurface>
    </CardActions>
  );
};

export const FactorReleaseCompareExport = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const tokenOps = useTokenOps();
  const [rightId, setRightId] = useState("");
  const [comparison, setComparison] = useState(null);
  if (!record) return null;

  const compare = async () => {
    if (!rightId.trim())
      return notify("A comparison release is required", "warning");
    try {
      setComparison(
        await carbonRequest(
          tokenOps.account.accessToken,
          `corporate-carbon/factor-releases/compare?leftId=${encodeURIComponent(record.id)}&rightId=${encodeURIComponent(rightId.trim())}`,
        ),
      );
    } catch (error) {
      notify(error.message, "warning");
    }
  };
  const exportRelease = async () => {
    try {
      const payload = await carbonRequest(
        tokenOps.account.accessToken,
        `corporate-carbon/factor-releases/${encodeURIComponent(record.id)}/export`,
      );
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
      });
      const href = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = href;
      anchor.download = `carbon-factor-release-${record.version || record.id}.json`;
      anchor.click();
      URL.revokeObjectURL(href);
    } catch (error) {
      notify(error.message, "warning");
    }
  };
  return (
    <Card style={{ marginTop: 16 }}>
      <CardContent>
        <Typography variant="h6">Compare and export</Typography>
        <CarbonTextInput
          label="Compare with release ID"
          name="compareReleaseId"
          value={rightId}
          onChange={(event) => setRightId(event.target.value)}
        />
        {comparison && (
          <Typography>
            {comparison.additions.length} addition ·{" "}
            {comparison.removals.length} removals · {comparison.changes.length}{" "}
            changes
          </Typography>
        )}
      </CardContent>
      <CardActions>
        <Button onClick={compare}>Compare</Button>
        <Button onClick={exportRelease}>Export release</Button>
      </CardActions>
    </Card>
  );
};

const StatusField = ({ record, source }) =>
  record ? <Chip size="small" label={record[source]} /> : null;

const CarbonFactorReleaseList = (props) => (
  <List
    {...props}
    bulkActionButtons={false}
    sort={{ field: "createdAt", order: "DESC" }}
    title="Emission factor release history"
  >
    <Datagrid rowClick="show">
      <TextField source="version" />
      <TextField source="name" />
      <StatusField source="status" />
      <TextField source="sourceFilename" label="Source workbook" />
      <DateField source="createdAt" showTime />
    </Datagrid>
  </List>
);

const CountSummary = () => {
  const record = useRecordContext();
  const counts = record?.validationSummary?.counts || {};
  return (
    <Typography>
      Combustion: {counts.combustion || 0} · Refrigerants:{" "}
      {counts.refrigerant || 0} · Electricity: {counts.electricity || 0} ·
      Total: {counts.total || 0}
    </Typography>
  );
};

const displayFactorValue = (value) =>
  value === null || value === undefined || value === "" ? "—" : String(value);

const FactorSource = ({ row }) =>
  row.sourceUrl ? (
    <Link href={row.sourceUrl} rel="noopener noreferrer" target="_blank">
      {row.source || row.sourceUrl}
    </Link>
  ) : (
    displayFactorValue(row.source)
  );

const DraftFactorEditButton = ({ category, releaseId, row }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const tokenOps = useTokenOps();
  const edit = async () => {
    const value = window.prompt("Factor value", row.value);
    if (value === null) return;
    const source = window.prompt("Source", row.source || "");
    if (source === null) return;
    const sourceUrl = window.prompt("Source URL", row.sourceUrl || "");
    if (sourceUrl === null) return;
    const notes = window.prompt("Notes", row.notes || "");
    if (notes === null) return;
    try {
      await carbonRequest(
        tokenOps.account.accessToken,
        `corporate-carbon/factor-releases/${releaseId}/factors/${category}/${row.id}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            value: Number(value),
            source: source.trim(),
            sourceUrl: sourceUrl.trim(),
            notes: notes.trim(),
          }),
        },
      );
      notify("Draft factor updated; revalidation is required", "info");
      refresh();
    } catch (error) {
      notify(error.message, "warning");
    }
  };
  return (
    <Button size="small" onClick={edit}>
      Edit
    </Button>
  );
};

export const FactorTable = ({
  category,
  editable = false,
  label,
  releaseId,
  rows = [],
  columns,
}) => (
  <CarbonSection
    title={label}
    description={`${rows.length} factor${rows.length === 1 ? "" : "s"} in this release.`}
  >
    <CarbonDataTable label={label}>
      <TableHead>
        <TableRow>
          {columns.map((column) => (
            <TableCell key={column.key}>{column.label}</TableCell>
          ))}
          {editable ? <TableCell>Actions</TableCell> : null}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length ? (
          rows.map((row) => (
            <TableRow key={row.id || row.key}>
              {columns.map((column) => (
                <TableCell key={column.key}>
                  {column.render
                    ? column.render(row)
                    : displayFactorValue(row[column.key])}
                </TableCell>
              ))}
              {editable ? (
                <TableCell>
                  <DraftFactorEditButton
                    category={category}
                    releaseId={releaseId}
                    row={row}
                  />
                </TableCell>
              ) : null}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={columns.length + (editable ? 1 : 0)}>
              No factors in this category.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </CarbonDataTable>
  </CarbonSection>
);

const sourceColumn = {
  key: "source",
  label: "Source",
  render: (row) => <FactorSource row={row} />,
};

export const combustionColumns = [
  { key: "fuelCode", label: "Fuel" },
  { key: "useType", label: "Use type" },
  { key: "blendType", label: "Blend" },
  { key: "geographyCode", label: "Geography" },
  { key: "year", label: "Year" },
  { key: "unit", label: "Unit" },
  { key: "value", label: "Factor value" },
  {
    key: "biogenic",
    label: "Biogenic",
    render: (row) => (row.biogenic ? "Yes" : "No"),
  },
  sourceColumn,
];

export const refrigerantColumns = [
  { key: "gasKey", label: "Refrigerant" },
  { key: "compositionType", label: "Composition" },
  { key: "description", label: "Description" },
  { key: "assessmentReport", label: "Assessment report" },
  { key: "year", label: "Year" },
  { key: "unit", label: "Unit" },
  { key: "value", label: "Factor value" },
  sourceColumn,
];

export const electricityColumns = [
  { key: "countryCode", label: "Country" },
  { key: "method", label: "Method" },
  { key: "dataset", label: "Dataset" },
  { key: "year", label: "Year" },
  { key: "unit", label: "Unit" },
  { key: "value", label: "Factor value" },
  sourceColumn,
];

export const FactorReleaseFactorTables = () => {
  const record = useRecordContext();
  if (!record) return null;
  return (
    <>
      <FactorTable
        category="combustion"
        columns={combustionColumns}
        editable={record.status === "draft"}
        label="Combustion factors"
        releaseId={record.id}
        rows={record.combustion || []}
      />
      <FactorTable
        category="refrigerant"
        columns={refrigerantColumns}
        editable={record.status === "draft"}
        label="Refrigerant factors"
        releaseId={record.id}
        rows={record.refrigerant || []}
      />
      <FactorTable
        category="electricity"
        columns={electricityColumns}
        editable={record.status === "draft"}
        label="Electricity factors"
        releaseId={record.id}
        rows={record.electricity || []}
      />
    </>
  );
};

export const CarbonFactorReleaseShow = (props) => (
  <Show {...props} actions={<FactorReleaseStatusActions />}>
    <SimpleShowLayout>
      <TextField source="version" />
      <TextField source="name" />
      <StatusField source="status" />
      <TextField source="sourceFilename" label="Source workbook" />
      <TextField source="checksum" />
      <TextField source="reviewChecksum" label="Reviewed checksum" />
      <TextField source="reviewNotes" label="Review notes" />
      <TextField source="publicationNotes" label="Publication notes" />
      <CountSummary />
      <FactorReleaseFactorTables />
      <FactorReleaseCompareExport />
      <DateField source="createdAt" showTime />
      <DateField source="reviewedAt" showTime />
      <DateField source="publishedAt" showTime />
      <DateField source="retiredAt" showTime />
    </SimpleShowLayout>
  </Show>
);

export const CarbonFactorReleaseCreate = ({ history }) => {
  const [version, setVersion] = useState("");
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const notify = useNotify();
  const tokenOps = useTokenOps();
  const submit = async (event) => {
    event.preventDefault();
    if (!file || !version.trim() || !name.trim())
      return notify("Version, name and workbook are required", "warning");
    const body = new FormData();
    body.append("version", version.trim());
    body.append("name", name.trim());
    body.append("file", file);
    setBusy(true);
    try {
      const result = await carbonRequest(
        tokenOps.account.accessToken,
        "corporate-carbon/factor-releases/import",
        { method: "POST", body },
      );
      notify(
        result.errors?.length
          ? `Draft imported with validation blockers: ${result.errors.join(", ")}`
          : `Workbook imported as draft (${result.acceptedRows} accepted rows)`,
        result.errors?.length ? "warning" : "info",
      );
      history.push(
        `/carbonFactorReleases/${result.id || result.release.id}/show`,
      );
    } catch (error) {
      notify(error.message, "warning");
    } finally {
      setBusy(false);
    }
  };
  return (
    <CarbonAdminPage
      title="Import emission factor workbook"
      description="The combustion, electricity and refrigerant factor sheets are imported as a draft. Validate them before submitting the release for review."
    >
      <CarbonForm aria-label="Factor release import form" onSubmit={submit}>
        <CarbonTextInput
          label="Version"
          name="version"
          value={version}
          onChange={(event) => setVersion(event.target.value)}
        />
        <CarbonTextInput
          label="Name"
          name="name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <CarbonFileInput
          accept=".xlsx,.xls"
          fileName={file?.name}
          label="Workbook"
          name="workbook"
          onChange={(event) => setFile(event.target.files[0] || null)}
        />
        <CarbonFormToolbar>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={busy}
          >
            {busy ? "Importing…" : "Import as draft"}
          </Button>
        </CarbonFormToolbar>
      </CarbonForm>
    </CarbonAdminPage>
  );
};

const carbonFactorReleaseResource = () => (
  <Resource
    name="carbonFactorReleases"
    options={{ label: "Factor data upload", menuGroup: "Bcome Carbon" }}
    list={CarbonFactorReleaseList}
    show={CarbonFactorReleaseShow}
    create={CarbonFactorReleaseCreate}
  />
);

export default carbonFactorReleaseResource;
